import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, MapPin, Package } from "lucide-react";
import Layout from "@/components/Layout";
import ReportButton from "@/components/ReportButton";
import Seo, { partSchema } from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PartDetail() {
    const { part_id } = useParams();
    const [part, setPart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contacting, setContacting] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        api.get(`/parts/${part_id}`)
            .then((r) => { if (!cancelled) setPart(r.data); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
        return () => { cancelled = true; };
    }, [part_id]);

    async function contactSeller() {
        if (!user) return navigate("/login");
        if (!part || part.owner_id === user.user_id) return;
        setContacting(true);
        try {
            const { data } = await api.post("/messages/conversations", {
                recipient_id: part.owner_id,
                initial_message: `Interested in your ${part.title}. Still available?`,
            });
            navigate(`/messages/${data.conversation_id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setContacting(false);
        }
    }

    if (loading) return <Layout><div className="p-8 text-onyx-500 text-sm">Loading…</div></Layout>;
    if (!part) return <Layout><div className="p-8 text-onyx-500 text-sm">Part not found.</div></Layout>;

    const isOwner = user && part.owner_id === user.user_id;

    return (
        <Layout>
            <Seo
                title={`${part.title} · ${part.condition} · SRT Finder Parts`}
                description={part.description?.slice(0, 160) || `${part.category} · ${part.condition} · $${part.price}`}
                canonicalPath={`/parts/${part.part_id}`}
                image={part.photos?.[0]}
                jsonLd={partSchema(part)}
                breadcrumbs={[
                    { name: "Marketplace", url: "/marketplace" },
                    { name: "Parts", url: "/marketplace?tab=parts" },
                    { name: part.title, url: `/parts/${part.part_id}` },
                ]}
            />
            <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="part-detail-page">
                <Link
                    to="/marketplace?tab=parts"
                    data-testid="part-back"
                    className="inline-flex items-center gap-2 text-sm text-onyx-500 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to Parts
                </Link>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                        {part.photos?.length ? (
                            part.photos.map((p, i) => (
                                <img
                                    key={i}
                                    src={p}
                                    alt={`${part.title} photo ${i + 1}`}
                                    className="w-full rounded-2xl object-cover"
                                    data-testid={`part-photo-${i}`}
                                />
                            ))
                        ) : (
                            <div className="aspect-[4/3] rounded-2xl bg-onyx-900 flex items-center justify-center">
                                <Package className="w-14 h-14 text-onyx-700" />
                            </div>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="chip">{part.category}</span>
                            <span className="chip">{part.condition}</span>
                            {part.status === "sold" && <span className="chip" style={{ background: "rgba(225,6,0,0.2)", color: "#FF6E68" }}>SOLD</span>}
                        </div>
                        <h1 className="text-3xl sm:text-4xl text-white leading-tight"
                            style={{ fontFamily: "'Alfa Slab One', serif", fontStyle: "italic" }}
                            data-testid="part-title">
                            {part.title}
                        </h1>
                        <div className="mt-3 text-3xl font-bold" style={{ color: "#E10600" }} data-testid="part-price">
                            ${part.price?.toLocaleString()}
                        </div>
                        {part.fits && (
                            <p className="text-sm text-onyx-500 mt-2">Fits: <span className="text-white">{part.fits}</span></p>
                        )}
                        {part.location && (
                            <p className="text-sm text-onyx-500 mt-1 inline-flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" /> {part.location}
                            </p>
                        )}
                        <p className="text-sm text-white/85 mt-4 leading-relaxed whitespace-pre-line" data-testid="part-description">
                            {part.description}
                        </p>

                        <div className="mt-6 flex flex-wrap gap-2">
                            {!isOwner && (
                                <button
                                    type="button"
                                    onClick={contactSeller}
                                    disabled={contacting}
                                    data-testid="part-contact-seller"
                                    className="btn-red px-5 py-3 inline-flex items-center gap-2 text-sm font-bold"
                                >
                                    <MessageSquare className="w-4 h-4" />
                                    {contacting ? "Opening…" : "Contact seller"}
                                </button>
                            )}
                            {!isOwner && (
                                <ReportButton targetType="listing" targetId={part.part_id} testid="part-report" />
                            )}
                        </div>

                        {part.seller && (
                            <div className="mt-6 pt-6 border-t border-white/5">
                                <div className="text-xs uppercase tracking-widest text-onyx-500 mb-2">Seller</div>
                                <Link to={`/users/${part.seller.user_id}`} className="text-sm text-white hover:text-hellcat">
                                    {part.seller.name}
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
